/**
 * A failure the mail server reported, in words a person at the shop can read.
 *
 * The worker stores what the server said (`TimeoutError: timed out`,
 * `SMTPRecipientsRefused: {'x@y': (550, b'...')}`): that is what support needs, and it
 * stays reachable under «Detalles técnicos». On the screen goes the kind of failure,
 * matched by shape, and what to do about it. Anything unrecognised falls into the
 * generic sentence: guessing a cause is worse than admitting there is none to give.
 */
/** «Gestion interna» to create, «Gestión interna» already there (`FolderConflict`). */
const LOOKALIKE_FOLDER = /ya existe «.+», que se lee igual/;

const MAIL_FAILURES: readonly (readonly [RegExp, string])[] = [
  // ADR 0026 of the back: the platform will not create a folder that reads the same as one
  // the owner already has («Gestion interna» next to «Gestión interna»), nor guess.
  [LOOKALIKE_FOLDER, 'en tu correo ya hay una carpeta que se llama casi igual'],
  [/timeout|timed out|tiempo de espera/i, 'el servidor de correo no ha respondido a tiempo'],
  [
    /authenticat|\b535\b|credentials|login failed|AUTHENTICATIONFAILED|contrasena|contraseña/i,
    'el servidor de correo no ha aceptado el usuario o la contraseña del buzón',
  ],
  [
    /RecipientsRefused|recipient|\b55[0-4]\b/i,
    'el servidor de correo ha rechazado la dirección de destino',
  ],
  [
    /gaierror|getaddrinfo|Name or service not known|nodename nor servname|No address associated/i,
    'no se encuentra el servidor de correo',
  ],
  [/certificate|SSL|TLS/i, 'la conexión segura con el servidor de correo ha fallado'],
  [
    /ConnectionRefused|ConnectionReset|Connection reset|Broken pipe|Connection aborted|refused|EOF/i,
    'se ha cortado la conexión con el servidor de correo',
  ],
];

/** «el servidor de correo no ha respondido a tiempo», or the generic sentence. */
export function mailFailureReason(raw: string): string {
  for (const [pattern, phrase] of MAIL_FAILURES) {
    if (pattern.test(raw)) return phrase;
  }
  return 'el servidor de correo ha devuelto un error';
}

/** The API could not hand the job to the worker (`no se pudo avisar al worker ...`). */
const QUEUE_DOWN = /no se pudo avisar/;

/**
 * Why a confirmed action did not happen (`action_error` when it failed), as one sentence
 * that ends with what to do. `reenvio fallido: ...` and `etiquetado fallido: ...` are
 * the two halves of `apply_actions`; `no se pudo avisar ...` is the API's own.
 */
export function actionFailureText(
  raw: string,
  { moves = false }: { moves?: boolean } = {},
): string {
  if (QUEUE_DOWN.test(raw)) {
    return 'el sistema no ha recibido el aviso. Pulsa «Reintentar».';
  }
  if (LOOKALIKE_FOLDER.test(raw)) {
    // Retrying changes nothing: somebody has to choose the folder's name first.
    return 'no se ha movido: en tu correo ya hay una carpeta que se llama casi igual. Avisa a quien administra la aplicación para que elija cuál usar.';
  }
  const forward = raw.includes('reenvio fallido');
  const label = raw.includes('etiquetado fallido');
  const what =
    forward && !label
      ? mailFailureReason(raw)
      : label && !forward
        ? `no se ha podido ${moves ? 'mover' : 'copiar'} a su carpeta: ${mailFailureReason(raw)}`
        : mailFailureReason(raw);
  return `${what}. Pulsa «Reintentar»; si vuelve a fallar, avisa a quien administra la aplicación.`;
}

/**
 * Why «Rescatar a la bandeja» did not work (`rescue_error`). The known refusals come
 * from `rescue_email` in the worker and from the API; the rest is the server's answer.
 */
export function rescueFailureText(raw: string): string {
  if (/buzon no configurado/.test(raw)) {
    return 'el buzón de este correo ya no está dado de alta en la aplicación.';
  }
  if (/sin UID|ya en la bandeja/.test(raw)) {
    return 'el correo ya está en la bandeja de entrada, o el buzón ya no lo tiene.';
  }
  if (/TRIAGE_ACTIONS_ENABLED|cerrojo/.test(raw)) {
    return 'el sistema todavía no mueve correo en los buzones. Rescátalo desde el programa de correo.';
  }
  if (QUEUE_DOWN.test(raw)) {
    return 'el sistema no ha recibido el aviso. Vuelve a pulsar «Rescatar a la bandeja».';
  }
  return `${mailFailureReason(raw)}. Vuelve a intentarlo en un rato; si sigue fallando, avisa a quien administra la aplicación.`;
}
